import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { CreditCard, Landmark } from 'lucide-react';
import { useConfirmCheckout, useMyPayment, useStartCheckout } from '@/hooks/usePayments';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { getApiErrorMessage } from '@/api/client';
import { formatEuro, paymentMethodLabel } from '@/lib/payments';
import type { BankTransfer } from '@/api/payments';

/** The UPN QR code: banking apps read it and fill in amount, IBAN, reference and purpose. */
function UpnQrImage({ payload }: { payload: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const options = { errorCorrectionLevel: 'M' as const, margin: 2, width: 240 };
    // The standard asks for version 15; fall back to automatic sizing if the text ever does not fit.
    QRCode.toDataURL(payload, { ...options, version: 15 })
      .catch(() => QRCode.toDataURL(payload, options))
      .then((url) => !cancelled && setSrc(url))
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (!src) return null;
  return <img src={src} alt="UPN QR koda za plačilo kotizacije" width={240} height={240} className="rounded-control border border-line bg-white" />;
}

function BankTransferDetails({ bank, amount }: { bank: BankTransfer; amount: string }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      <UpnQrImage payload={bank.upnQrPayload} />
      <dl className="grid content-start gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-fg-muted">Znesek</dt>
        <dd className="font-medium text-fg">{amount}</dd>
        <dt className="text-fg-muted">Prejemnik</dt>
        <dd className="text-fg">{bank.recipientName}</dd>
        <dt className="text-fg-muted">IBAN</dt>
        <dd className="font-mono text-fg">{bank.iban.replace(/(.{4})/g, '$1 ').trim()}</dd>
        <dt className="text-fg-muted">Sklic</dt>
        <dd className="font-mono text-fg">{bank.reference}</dd>
        <dt className="text-fg-muted">Namen</dt>
        <dd className="text-fg">{bank.purpose}</dd>
      </dl>
    </div>
  );
}

/**
 * The competitor's way to pay the entry fee. Renders nothing at all unless the organizer has set a fee and turned on at
 * least one way of paying - so a competition without payments looks exactly as before.
 */
export function PaymentCard({ competitionId }: { competitionId: string }) {
  const [params, setParams] = useSearchParams();
  const returnedFromCard = params.get('placilo') === 'uspesno';
  const payment = useMyPayment(competitionId, { poll: returnedFromCard });
  const checkout = useStartCheckout(competitionId);
  const confirm = useConfirmCheckout(competitionId);

  // Back from Stripe: ask the server to check that very session, so "paid" shows even if the webhook is late or missing.
  const sessionId = params.get('session_id');
  const confirmed = useRef(false);
  useEffect(() => {
    if (!returnedFromCard || !sessionId || confirmed.current) return;
    confirmed.current = true;
    confirm.mutate(sessionId, {
      onSettled: () => setParams((p) => { p.delete('session_id'); return p; }, { replace: true }),
    });
  }, [returnedFromCard, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = payment.data;
  if (!data || data.entryFeeCents == null) return null;
  if (!data.hasPaid && !data.cardAvailable && !data.bankTransfer) return null;

  const amount = formatEuro(data.entryFeeCents);

  async function payByCard() {
    const url = await checkout.mutateAsync().catch(() => null);
    if (url) window.location.assign(url);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <h2 className="font-semibold text-fg">Kotizacija {amount}</h2>
        {data.hasPaid && <Badge tone="Accepted">Plačano{data.paidVia ? ` (${paymentMethodLabel(data.paidVia)})` : ''}</Badge>}
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {data.hasPaid ? (
          <p className="text-sm text-fg-secondary">Hvala, kotizacija je plačana.</p>
        ) : (
          <>
            {returnedFromCard && (
              <Alert variant="success">
                Plačilo s kartico je v obdelavi. Oznaka Plačano se prikaže sama, ko ga banka potrdi.
              </Alert>
            )}
            {params.get('placilo') === 'preklicano' && <Alert variant="info">Plačilo je bilo preklicano. Poskusite znova, kadar želite.</Alert>}
            {checkout.isError && <Alert variant="error">{getApiErrorMessage(checkout.error)}</Alert>}

            {data.cardAvailable && (
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-2 text-sm font-medium text-fg">
                  <CreditCard size={16} aria-hidden="true" /> S kartico
                </p>
                <p className="text-sm text-fg-muted">Plačilo poteka na varni strani Stripe. Podatkov o kartici ne vidimo.</p>
                <Button className="self-start" isLoading={checkout.isPending} onClick={payByCard}>
                  Plačaj {amount}
                </Button>
              </div>
            )}

            {data.bankTransfer && (
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-2 text-sm font-medium text-fg">
                  <Landmark size={16} aria-hidden="true" /> Z bančnim nakazilom
                </p>
                <p className="text-sm text-fg-muted">
                  Skenirajte kodo v aplikaciji svoje banke ali nakažite ročno. Organizator plačilo označi, ko ga prejme.
                </p>
                <BankTransferDetails bank={data.bankTransfer} amount={amount} />
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
